'use client';

import Image from 'next/image';
import portal from '../../public/images/portal.svg';
import fatigue_detection from '../../public/images/fatigueDetection2.svg';
import Modal from 'react-modal';
import { useState, useEffect } from 'react';

type Project = {
  name: string;
  description: string;
  imageSrc: any;
  imageAlt: string;
  href: string;
};

const callouts: Project[] = [

  {
    name: 'Maladaptive Game',
    description:
        "Maladaptive was a game we created during an university course: Software Engineering. We prioritized creating everything from scratch. My work included Blender 3D modeling, Level design and audio post-processing. The game is proof of concept and therefore contains some scuffed elements.",
    imageSrc: portal,
    imageAlt: 'Screenshot from game.',
    href: 'https://github.com/tasnaps/Maladaptive',
  },
  {
    name: 'Eye Tracking',
    description:
        "Eye tracking experiment. Compare text that is generated via LLM and human written text.",
    imageSrc: fatigue_detection,
    imageAlt: 'Eye tracking experiment.',
    href: 'https://github.com/tasnaps/eye_tracking',
  },

];

Modal.setAppElement('#__next');

export default function ProjectsPreview() {
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  useEffect(() => {
    document.body.style.overflow = activeProject ? 'hidden' : 'unset';
  }, [activeProject]);

  function openModal(project: Project) {
    setActiveProject(project);
  }

  function closeModal() {
    setActiveProject(null);
  }

  return (
      <div className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto py-16 sm:py-24 lg:py-32 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Projects
            </h2>
            <div className="mt-14 space-y-12 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-6">
              <Modal
                  isOpen={!!activeProject}
                  onRequestClose={closeModal}
                  className="card lg:card-side bg-base-100 shadow-xl glass relative z-50 max-w-lg p-6 rounded-lg"
                  overlayClassName="fixed inset-0 flex justify-center items-center z-40 bg-black/70"
                  contentLabel="Project Details"
                  ariaHideApp={false}
              >
                {activeProject && (
                    <div className="z-50">
                      <h2 className="text-2xl font-bold text-white">{activeProject.name}</h2>
                      <p className="mt-4 text-white">{activeProject.description}</p>
                      <button
                          type="button"
                          className="mt-6 btn bg-red-500 text-white px-4 py-2 rounded-lg"
                          onClick={closeModal}
                      >
                        Close
                      </button>
                      <button
                          type="button"
                          className="mt-6 btn bg-black text-white px-4 py-2 rounded-lg ml-2"
                          onClick={() => window.open(activeProject.href, '_blank')}
                      >
                        Github
                      </button>
                    </div>
                )}
              </Modal>

              {callouts.map((callout) => (
                  <div key={callout.name} className="group relative">
                    <div className="relative rounded-lg bg-white overflow-hidden group-hover:opacity-75">
                      <button
                          type="button"
                          onClick={() => openModal(callout)}
                          className="w-full h-full flex items-center justify-center p-4"
                      >
                        <div className="w-full flex items-center justify-center">
                          <div className="w-3/4 sm:w-full aspect-[4/3]">
                            <Image
                                src={callout.imageSrc}
                                alt={callout.imageAlt}
                                className="object-contain w-full h-full"
                                sizes="(max-width: 640px) 75vw, (max-width: 1024px) 50vw, 33vw"
                            />
                          </div>
                        </div>
                      </button>
                    </div>
                    <h3 className="mt-6 text-sm text-gray-500">{callout.name}</h3>
                  </div>
              ))}
            </div>
          </div>
        </div>
      </div>
  );
}
